/**
 * <flash-sale-banner> — custom element for active flash sale + countdown + coupon.
 */
if (!customElements.get('flash-sale-banner')) {
  class FlashSaleBanner extends HTMLElement {
    constructor() {
      super();
      this.timerId = null;
      this.copyResetId = null;
      this.onCouponClick = this.onCouponClick.bind(this);
    }

    connectedCallback() {
      this.titleEl = this.querySelector('[data-fsb-title]');
      this.countdownEl = this.querySelector('[data-fsb-countdown]');
      this.couponEl = this.querySelector('[data-fsb-coupon]');
      this.couponCodeEl = this.querySelector('[data-fsb-coupon-code]');

      this.apiUrl = '/apps/app-api/api/getActiveFlashSales';
      this.bannerId = (this.dataset.bannerId || '').trim();
      this.shopDomain = this.dataset.shopDomain || '';
      this.fallbackText = this.dataset.fallbackText || '';
      this.couponCode = (this.dataset.couponCode || '').trim();
      this.showCoupon = this.dataset.showCoupon !== 'false';
      this.copiedLabel = this.dataset.copiedLabel || 'Copied!';
      this.hideOnExpire = this.dataset.hideOnExpire !== 'false';
      this.isDesignMode = this.dataset.designMode === 'true';

      // Stay hidden until a valid active sale is confirmed
      this.hide();
      this.init();
    }

    disconnectedCallback() {
      this.destroy();
    }

    init() {
      this.bindCoupon();

      // app_only / web_only are handled by global CSS in mobile-app-scripts
      // (.app-only / .web-only + html.is-mobile-app). Do not override display here.

      if (!this.bannerId) {
        if (this.isDesignMode) {
          this.showPreview();
        } else {
          this.hide();
        }
        return;
      }

      this.fetchSale()
        .then((data) => this.handleResponse(data))
        .catch(() => {
          this.hide();
        });
    }

    bindCoupon() {
      if (!this.couponEl) return;
      this.couponEl.removeEventListener('click', this.onCouponClick);
      this.couponEl.addEventListener('click', this.onCouponClick);
    }

    async fetchSale() {
      // App proxy: /apps/app-api/* -> https://resin-rockers--development.gadget.app/*
      // Shopify also injects `shop`; we send shopDomain as a fallback for the route.
      const params = new URLSearchParams({
        bannerId: this.bannerId,
      });

      if (this.shopDomain) {
        params.set('shopDomain', this.shopDomain);
      }

      const response = await fetch(`${this.apiUrl}?${params.toString()}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });

      let payload = null;
      try {
        payload = await response.json();
      } catch (err) {
        payload = null;
      }

      // Return JSON even for 404 so handleResponse can hide the banner
      if (payload && typeof payload === 'object') {
        return payload;
      }

      if (!response.ok) {
        throw new Error(`Flash sale request failed (${response.status})`);
      }

      throw new Error('Flash sale response was empty');
    }

    handleResponse(payload) {
      // Hide completely when sale is missing (e.g. statusCode 404)
      if (!payload || payload.statusCode !== 200) {
        this.hide();
        return;
      }

      const data = payload.data;
      if (!data) {
        this.hide();
        return;
      }

      const metadata = data.metadata || {};

      // Show whenever response data exists and bannerEnabled is true
      if (!metadata.bannerEnabled) {
        this.hide();
        return;
      }

      const showCountdown = Boolean(metadata.bannerShowCountdown);
      const endTimeUtc = metadata.endTime;

      // Don't flash a banner that is already expired
      if (showCountdown && endTimeUtc && this.hideOnExpire) {
        const remaining = this.getRemainingSeconds(endTimeUtc);
        if (remaining !== null && remaining <= 0) {
          this.hide();
          return;
        }
      }

      this.render({
        title: this.fallbackText,
        showCountdown,
        endTimeUtc,
      });
    }

    /**
     * Parse API UTC datetime into a local Date.
     * Accepts ISO strings like "2026-07-18T05:54:00.000Z".
     * If timezone is missing, treat the value as UTC.
     */
    parseUtcToLocal(utcValue) {
      if (!utcValue) return null;

      let value = String(utcValue).trim();
      if (!value) return null;

      // Ensure UTC when no timezone offset is present
      const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
      if (!hasTimezone) {
        value = `${value}Z`;
      }

      const localDate = new Date(value);
      if (Number.isNaN(localDate.getTime())) return null;

      return localDate;
    }

    getRemainingSeconds(endTimeUtc) {
      const localEnd = this.parseUtcToLocal(endTimeUtc);
      if (!localEnd) return null;

      // Compare UTC instant against system clock (local timezone aware)
      return Math.floor((localEnd.getTime() - Date.now()) / 1000);
    }

    render({ title, showCountdown, endTimeUtc }) {
      if (this.titleEl) {
        this.titleEl.textContent = title || this.fallbackText || '';
      }

      this.classList.toggle('has-countdown', Boolean(showCountdown));

      if (showCountdown && endTimeUtc && this.countdownEl) {
        const remaining = this.getRemainingSeconds(endTimeUtc);
        if (remaining !== null && remaining <= 0 && this.hideOnExpire) {
          this.hide();
          return;
        }

        this.countdownEl.hidden = false;
        this.renderCoupon();
        this.show();
        this.startCountdown(endTimeUtc);
        return;
      }

      if (this.countdownEl) {
        this.countdownEl.hidden = true;
        this.stopCountdown();
      }

      this.renderCoupon();
      this.show();
    }

    renderCoupon() {
      if (!this.couponEl) return;

      if (!this.showCoupon || !this.couponCode) {
        this.couponEl.hidden = true;
        return;
      }

      if (this.couponCodeEl) {
        this.couponCodeEl.textContent = this.couponCode;
      }
      this.couponEl.hidden = false;
      this.couponEl.setAttribute('aria-label', `Copy coupon code ${this.couponCode}`);
    }

    showPreview() {
      if (this.titleEl) {
        this.titleEl.textContent = this.fallbackText || 'Hurry, limited time!';
      }
      if (this.countdownEl) {
        this.countdownEl.hidden = false;
        this.updateCountdownUnits(11 * 3600 + 5 * 60 + 43);
      }
      this.classList.add('has-countdown');
      this.renderCoupon();
      this.show();
    }

    async onCouponClick(event) {
      event.preventDefault();
      event.stopPropagation();
      if (!this.couponCode) return;

      const original = this.couponCode;
      let copied = false;

      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(this.couponCode);
          copied = true;
        }
      } catch (err) {
        copied = false;
      }

      if (!copied) {
        copied = this.copyFallback(this.couponCode);
      }

      if (!copied || !this.couponCodeEl) return;

      this.couponEl.classList.add('is-copied');
      this.couponCodeEl.textContent = this.copiedLabel;

      window.clearTimeout(this.copyResetId);
      this.copyResetId = window.setTimeout(() => {
        this.couponCodeEl.textContent = original;
        this.couponEl.classList.remove('is-copied');
      }, 1600);
    }

    copyFallback(text) {
      const input = document.createElement('textarea');
      input.value = text;
      input.setAttribute('readonly', '');
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.select();
      let ok = false;
      try {
        ok = document.execCommand('copy');
      } catch (err) {
        ok = false;
      }
      document.body.removeChild(input);
      return ok;
    }

    startCountdown(endTimeUtc) {
      this.stopCountdown();

      const localEnd = this.parseUtcToLocal(endTimeUtc);
      if (!localEnd) {
        if (this.countdownEl) this.countdownEl.hidden = true;
        return;
      }

      const tick = () => {
        const remaining = this.getRemainingSeconds(endTimeUtc);

        if (remaining === null) {
          this.stopCountdown();
          if (this.countdownEl) this.countdownEl.hidden = true;
          return;
        }

        if (remaining <= 0) {
          this.updateCountdownUnits(0);
          this.stopCountdown();
          if (this.hideOnExpire) this.hide();
          return;
        }

        this.updateCountdownUnits(remaining);
      };

      tick();
      this.timerId = window.setInterval(tick, 1000);
    }

    updateCountdownUnits(totalSeconds) {
      if (!this.countdownEl) return;
      const s = Math.max(0, totalSeconds);
      const units = {
        d: Math.floor(s / 86400),
        h: Math.floor((s % 86400) / 3600),
        m: Math.floor((s % 3600) / 60),
        s: s % 60,
      };

      Object.keys(units).forEach((key) => {
        const el = this.countdownEl.querySelector(`[data-t="${key}"]`);
        if (el) el.textContent = this.pad(units[key]);
      });
    }

    pad(n) {
      return (n < 10 ? '0' : '') + n;
    }

    stopCountdown() {
      if (this.timerId) {
        window.clearInterval(this.timerId);
        this.timerId = null;
      }
    }

    show() {
      // API visibility only — device (app-only/web-only) stays on the
      // .flash-sale-banner__device wrapper via global mobile-app-scripts CSS.
      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');
      this.toggleSectionReady(true);
      document.body.classList.add('flash-sale-banner--visible');
    }

    hide() {
      this.stopCountdown();
      this.hidden = true;
      this.setAttribute('aria-hidden', 'true');
      this.toggleSectionReady(false);
      document.body.classList.remove('flash-sale-banner--visible');
    }

    toggleSectionReady(ready) {
      const section = this.closest('.shopify-section');
      if (!section) return;
      // Class-only — no inline display, so global .app-only / .web-only still win
      section.classList.toggle('flash-sale-banner-section--ready', ready);
    }

    destroy() {
      this.stopCountdown();
      window.clearTimeout(this.copyResetId);
      if (this.couponEl) {
        this.couponEl.removeEventListener('click', this.onCouponClick);
      }
    }
  }

  customElements.define('flash-sale-banner', FlashSaleBanner);
  window.FlashSaleBanner = FlashSaleBanner;
}